/**
 * Bulk request reports for the redesigned list: fetch new-reports, 5s polling while processing,
 * (completed Details only), and dismiss via markViewed.
 */
// eslint-disable-next-line
import { makeAutoObservable, runInAction } from "mobx";
import { rootContainer, TYPE } from "../../../../services/container/root";
import BulkRmaActionService from "../../../../services/api/bulk-rma-action-service";
import {
    BULK_REQUEST_STATUS,
    IBulkNewReport,
    IBulkRequestReturnOutcome,
    BULK_RMA_ACTION,
    IStartBulkActionPayload,
    IStartBulkActionResult,
} from "../../../../services/api/bulk-rma-action-types";
import currentPortalStore from "../../../../stores/current-portal-store";
import { FEATURES } from "../../../../models/Plan";
import rmaPagedListStore from "./rma-tabs-paged-list-store";
import { BulkStatusPanelStore } from "./bulk-status-panel-store";

const POLL_INTERVAL_MS = 5000;

class BulkRmaActionStore {
    private _newReport: IBulkNewReport | null = null;
    private _isPanelOpen = false;
    private _panelStore: BulkStatusPanelStore | null = null;
    private _isPanelReturnsLoading = false;
    private _isStarting = false;
    private _isDismissing = false;
    private _pollTimer: ReturnType<typeof setInterval> | null = null;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get _service(): BulkRmaActionService {
        return rootContainer.get<BulkRmaActionService>(TYPE.BulkRmaActionService);
    }

    public get isFeatureEnabled(): boolean {
        return currentPortalStore.isFeatureAllowed(FEATURES.BETA_BULK_MARK_DONE);
    }

    /** The single new-report for toast/panel, when one exists. */
    public get newReport(): IBulkNewReport | null {
        return this._newReport;
    }

    private get _hasNewReport(): boolean {
        return this._newReport !== null;
    }

    public get isPanelOpen(): boolean {
        return this._isPanelOpen;
    }

    public get panelStore(): BulkStatusPanelStore | null {
        return this._panelStore;
    }

    public get isPanelReturnsLoading(): boolean {
        return this._isPanelReturnsLoading;
    }

    public get isStarting(): boolean {
        return this._isStarting;
    }

    public get isDismissing(): boolean {
        return this._isDismissing;
    }

    /** Terminal per-return outcomes for the current report's status panel. */
    private async _fetchRequestReturns(): Promise<IBulkRequestReturnOutcome[]> {
        const report = this.newReport;
        if (!report) {
            return [];
        }
        return this._service.fetchRequestReturns(report.request_id);
    }

    public async openPanel(): Promise<void> {
        const report = this.newReport;
        if (
            report?.status !== BULK_REQUEST_STATUS.COMPLETED
            && report?.status !== BULK_REQUEST_STATUS.CANCELLED
        ) {
            return;
        }

        runInAction(() => {
            this._isPanelOpen = true;
            this._isPanelReturnsLoading = true;
            this._panelStore = null;
        });

        try {
            const returns = await this._fetchRequestReturns();
            runInAction(() => {
                const currentReport = this.newReport;
                if (
                    currentReport
                    && currentReport.request_id === report.request_id
                    && this._isPanelOpen
                ) {
                    this._panelStore = new BulkStatusPanelStore(currentReport, returns);
                }
                this._isPanelReturnsLoading = false;
            });
        } catch (err) {
            console.error(err);
            runInAction(() => {
                this._isPanelReturnsLoading = false;
            });
        }
    }

    public closePanel(): void {
        this._isPanelOpen = false;
        this._panelStore = null;
        this._isPanelReturnsLoading = false;
    }

    /** Fetch the report the merchant UI should show (processing or completed unviewed). */
    public async loadNewReports(): Promise<void> {
        try {
            const report = await this._service.fetchNewReports();
            runInAction(() => {
                const previousRequestId = this._newReport?.request_id;
                this._newReport = report;
                if (report?.request_id !== previousRequestId) {
                    this._panelStore = null;
                }
                rmaPagedListStore.bulkSelectionStore.setBlocked(this._hasNewReport);
            });
            this._syncPolling();
        } catch (err) {
            console.error(err);
        }
    }

    public stopPolling(): void {
        if (this._pollTimer) {
            clearInterval(this._pollTimer);
            this._pollTimer = null;
        }
    }

    public async startAction(
        action: BULK_RMA_ACTION,
        payload: IStartBulkActionPayload,
    ): Promise<IStartBulkActionResult> {
        runInAction(() => {
            this._isStarting = true;
        });
        const result = await this._service.startAction(action, payload);
        runInAction(() => {
            this._isStarting = false;
        });
        if (result.started) {
            await this.loadNewReports();
        }
        return result;
    }

    public async dismissNewReport(): Promise<boolean> {
        const report = this.newReport;
        if (
            !report
            || (report.status !== BULK_REQUEST_STATUS.COMPLETED
                && report.status !== BULK_REQUEST_STATUS.CANCELLED)
        ) {
            return false;
        }
        runInAction(() => {
            this._isDismissing = true;
        });
        try {
            await this._service.markRequestViewed(report.request_id);
            runInAction(() => {
                this._isPanelOpen = false;
                this._panelStore = null;
                this._isPanelReturnsLoading = false;
                this._isDismissing = false;
            });
            await this.loadNewReports();
            return true;
        } catch (err) {
            console.error(err);
            runInAction(() => {
                this._isDismissing = false;
            });
            return false;
        }
    }

    private _syncPolling(): void {
        this.stopPolling();
        if (!this.isFeatureEnabled) {
            return;
        }
        if (this._newReport?.status !== BULK_REQUEST_STATUS.PROCESSING) {
            return;
        }
        this._pollTimer = setInterval(() => {
            void this.loadNewReports();
        }, POLL_INTERVAL_MS);
    }
}

export default new BulkRmaActionStore();
